/**
 * PatientApp component
 * Patient portal for check-in conversations
 */

import React, { useState, useEffect, useRef } from "react";
import { Sidebar } from "./shared/Sidebar";
import { ErrorModal } from "./ui/ErrorModal";
import { conversationAgent, summarizerAgent } from "../agents";
import {
  updateQuestionStatus,
  subscribeActiveQuestion,
  subscribeCompletedQuestions,
} from "../services/questions";
import {
  addMessage,
  updateGoalIndex,
  subscribeConversation,
  getConversation,
} from "../services/conversations";
import { getGoals, updateGoal, getPatientMemory, addPatientMemory } from "../services/goals";
import { saveSummary } from "../services/summaries";
import { now, getCurrentDate, getInitials } from "../utils/helpers";

export const PatientApp = ({ user, onLogout }) => {
  const [page, setPage] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(true);
  const [uiError, setUiError] = useState("");
  const [historyItems, setHistoryItems] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedHistoryId, setSelectedHistoryId] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [conv, setConv] = useState(null);
  const [activeQ, setActiveQ] = useState(null);
  const messagesEndRef = useRef(null);

  const formatCreatedAt = (ts) => {
    const seconds = ts?.seconds;
    if (!seconds) return "Unknown date";
    return new Date(seconds * 1000).toLocaleString();
  };

  const getConversationPreview = (messages) => {
    const firstUser = (messages || []).find((m) => m.role === "user" && String(m.text || "").trim());
    const fallback = (messages || []).find((m) => String(m.text || "").trim());
    const preview = String(firstUser?.text || fallback?.text || "Session conversation").trim();
    return preview.length > 64 ? `${preview.slice(0, 64)}...` : preview;
  };

  const toFriendlySendError = (error) => {
    const message = String(error?.message || "");
    const lower = message.toLowerCase();

    if (lower.includes("limit: 0")) {
      return "Our AI agent is temporarily unavailable due to service limits. Please try again shortly (60 second). Then resend your last message.";
    }
    if (lower.includes("quota") || lower.includes("resource_exhausted") || lower.includes("429")) {
      return "Our AI agent is handling high traffic right now. Please wait 60 seconds, then resend your last message.";
    }
    if (lower.includes("503") || lower.includes("high demand") || lower.includes("temporarily unavailable") || lower.includes("unavailable")) {
      return "Our AI agent is currently experiencing high demand. Please wait 60 seconds, then resend your last message.";
    }
    if (lower.includes("api key") || lower.includes("permission_denied") || lower.includes("401") || lower.includes("403")) {
      return "Our AI agent is temporarily unavailable due to configuration access. Please try again soon, then resend your last message.";
    }
    if (lower.includes("model") && (lower.includes("not found") || lower.includes("unsupported") || lower.includes("deprecated"))) {
      return "Our AI agent is being updated and is briefly unavailable. Please wait 60 seconds, then resend your last message.";
    }
    if (lower.includes("failed to fetch") || lower.includes("network")) {
      return "Network connection issue while contacting our AI agent. Please check your internet, wait 60 seconds, then resend your last message.";
    }

    return "Message could not be sent right now. Please wait 60 seconds, then resend your last message.";
  };

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scrollToBottom, [messages, isTyping]);

  useEffect(() => {
    let unsubscribeConversation = null;
    let retryCount = 0;
    let retryTimeout = null;

    const setupActiveQSubscriber = () => {
      const unsubscribeActiveQ = subscribeActiveQuestion(
        user.id,
        (q) => {
          setIsLoadingChat(false);
          setUiError("");
          retryCount = 0; // Reset retry count on success
          setActiveQ(q);

          if (unsubscribeConversation) {
            unsubscribeConversation();
            unsubscribeConversation = null;
          }

          if (!q?.convId) {
            setConv(null);
            setMessages([]);
            setShowSettings(false);
            return;
          }

          unsubscribeConversation = subscribeConversation(
            q.convId,
            (conversation) => {
              setConv(conversation);
              setMessages(conversation?.messages || []);
            },
            (error) => {
              // Retry logic for conversation subscription
              retryCount += 1;
              if (retryCount <= 2) {
                const delay = Math.pow(2, retryCount) * 1000;
                retryTimeout = setTimeout(() => {
                  setupActiveQSubscriber();
                }, delay);
              } else {
                setUiError("Chat sync unavailable. Please refresh the page.");
              }
            }
          );
        },
        (error) => {
          // Retry logic for active question subscription
          retryCount += 1;
          if (retryCount <= 3) {
            const delay = Math.pow(2, retryCount) * 1000;
            retryTimeout = setTimeout(() => {
              setupActiveQSubscriber();
            }, delay);
          } else {
            setIsLoadingChat(false);
            setUiError("Unable to load check-in status. Please refresh the page.");
          }
        }
      );
      return unsubscribeActiveQ;
    };

    let unsubscribeActiveQ = setupActiveQSubscriber();

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      unsubscribeActiveQ();
      if (unsubscribeConversation) unsubscribeConversation();
    };
  }, [user.id]);

  useEffect(() => {
    let retryCount = 0;
    let retryTimeout = null;

    const setupHistorySubscriber = () => {
      const unsubscribeCompleted = subscribeCompletedQuestions(
        user.id,
        async (completed) => {
          try {
            setUiError(""); // Clear error on successful data
            retryCount = 0; // Reset retry count
            const sorted = [...completed].sort((a, b) => {
              const aSec = a?.createdAt?.seconds || 0;
              const bSec = b?.createdAt?.seconds || 0;
              return bSec - aSec;
            });

            const enriched = await Promise.all(
              sorted.map(async (q) => {
                const convData = q.convId ? await getConversation(q.convId).catch(() => null) : null;
                return {
                  id: q.id,
                  createdAt: q.createdAt,
                  messages: convData?.messages || [],
                };
              })
            );

            setHistoryItems(enriched);
            setSelectedHistoryId((prev) => {
              if (prev && enriched.some((item) => item.id === prev)) return prev;
              return enriched[0]?.id || "";
            });
          } catch (error) {
            console.error("History loading error:", error);
            setUiError("Could not load conversation history. Please refresh.");
          } finally {
            setIsLoadingHistory(false);
          }
        },
        (error) => {
          console.error("Completed question subscription error:", error);
          // Retry logic for completed questions subscription
          retryCount += 1;
          if (retryCount <= 3) {
            const delay = Math.pow(2, retryCount) * 1000;
            retryTimeout = setTimeout(() => {
              setupHistorySubscriber();
            }, delay);
          } else {
            setIsLoadingHistory(false);
            setUiError("History is temporarily unavailable. Please refresh the page.");
          }
        }
      );
      return unsubscribeCompleted;
    };

    let unsubscribeCompleted = setupHistorySubscriber();

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      unsubscribeCompleted();
    };
  }, [user.id]);

  const sendMessage = async () => {
    if (!input.trim() || !conv || isTyping) return;
    setUiError("");

    const userMsg = { role: "user", text: input.trim(), time: now() };
    const updatedMsgs = [...messages, userMsg];

    setMessages(updatedMsgs);
    setInput("");
    setIsTyping(true);

    try {
      await addMessage(conv.id, userMsg);
      await new Promise((r) => setTimeout(r, 400));

      const q = activeQ;
      const goals = await getGoals(conv.questionId);
      const gIdx = conv.currentGoalIndex || 0;
      const currentGoal = goals[gIdx];

      if (!currentGoal) {
        return;
      }

      // Get memory for RAG context
      const memChunks = (await getPatientMemory(user.id, 5)).map((m) => m.summary);
      const patient = { name: user.name };

      const remainingGoals = goals.slice(gIdx + 1).map((g) => g.text);
      const aiResult = await conversationAgent({
        currentGoal: currentGoal.text,
        remainingGoals,
        messages: updatedMsgs,
        memoryChunks: memChunks,
        patientName: patient.name,
      });

      const goalDone = Boolean(aiResult.goalComplete);
      const aiText = String(aiResult.text || "").trim();
      const aiMsg = { role: "ai", text: aiText, time: now() };

      await addMessage(conv.id, aiMsg);
      const newMsgs = [...updatedMsgs, aiMsg];
      setMessages(newMsgs);

      if (goalDone) {
        // Mark goal as done and capture insights
        const insightText = updatedMsgs.filter((m) => m.role === "user").map((m) => m.text).join(" | ");
        await updateGoal(conv.questionId, gIdx, { status: "done", insights: insightText });

        const nextIdx = gIdx + 1;
        if (nextIdx < goals.length) {
          await updateGoal(conv.questionId, nextIdx, { status: "active" });
          await updateGoalIndex(conv.id, nextIdx);
          conv.currentGoalIndex = nextIdx;
        } else {
          // All goals done - summarize
          const summary = await summarizerAgent(q.text, goals, patient.name, memChunks);
          await saveSummary(conv.questionId, summary);

          // Add to patient memory for RAG
          await addPatientMemory(user.id, {
            date: getCurrentDate(),
            summary: `Session summary: ${summary.slice(0, 200)}`,
            topics: goals.map((g) => g.text),
          });

          // Mark question as completed
          await updateQuestionStatus(conv.questionId, "completed");

          const doneMsg = {
            role: "ai",
            text: "Thank you for your answers. That's all for now. Your check-in is complete, and your doctor can review it.",
            time: now(),
          };
          await addMessage(conv.id, doneMsg);
          setMessages([...newMsgs, doneMsg]);
          setActiveQ(null);
          return;
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setUiError(toFriendlySendError(error));
    } finally {
      setIsTyping(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const noConv = !activeQ || !conv;
  const selectedHistory = historyItems.find((item) => item.id === selectedHistoryId) || null;

  const navItems = [
    {
      id: "messages",
      label: "Messages",
      active: page === "messages",
      onClick: () => setPage("messages"),
      status: activeQ ? "active" : "idle",
    },
    {
      id: "history",
      label: "History",
      active: page === "history",
      onClick: () => setPage("history"),
      status: historyItems.length ? "active" : "idle",
    },
  ];

  return (
    <div className="mx-auto flex max-w-7xl pt-[4.25rem] lg:grid lg:min-h-screen lg:grid-cols-[18rem_minmax(0,1fr)] lg:pt-0">
      <Sidebar
        logoText="Patient Messages"
        role="patient"
        user={user}
        nav={navItems}
        onLogout={onLogout}
      />

      <div className="flex w-full flex-col overflow-y-auto pb-24 lg:pb-0">
        {page === "history" ? (
          <div className="mx-auto grid w-full max-w-5xl gap-4 px-4 pb-6 pt-4 sm:px-6 sm:pt-6">
            <div className="text-2xl font-semibold tracking-tight text-slate-800">Conversation History</div>
            {isLoadingHistory ? (
              <div className="rounded-2xl border border-sky-100 bg-white px-4 py-10 text-center text-sm text-slate-500">Loading history...</div>
            ) : historyItems.length === 0 ? (
              <div className="rounded-2xl border border-sky-100 bg-white px-4 py-10 text-center text-sm text-slate-500">No completed check-ins yet.</div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <div className="overflow-hidden rounded-2xl border border-sky-100 bg-white shadow-sm">
                  {historyItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setSelectedHistoryId(item.id)}
                      className={`w-full border-b border-sky-50 px-4 py-3 text-left transition last:border-b-0 ${item.id === selectedHistoryId ? "bg-sky-50" : "hover:bg-slate-50"}`}
                    >
                      <div className="mb-1 text-sm font-semibold text-slate-800">
                        {getConversationPreview(item.messages)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatCreatedAt(item.createdAt)}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm">
                  {!selectedHistory ? (
                    <div className="px-4 py-10 text-center text-sm text-slate-500">Select a session to view details.</div>
                  ) : (
                    <>
                      <div className="mb-1 text-lg font-semibold text-slate-800">Conversation</div>
                      <div className="mb-3 text-xs text-slate-500">
                        {formatCreatedAt(selectedHistory.createdAt)}
                      </div>
                      <div className="grid max-h-[56vh] gap-2 overflow-y-auto pr-1">
                        {selectedHistory.messages.length === 0 ? (
                          <div className="px-4 py-10 text-center text-sm text-slate-500">No saved messages for this session yet. Check your current chat in the Messages tab.</div>
                        ) : (
                          selectedHistory.messages.map((m, i) => (
                            <div key={`${selectedHistory.id}-${i}`} className={`max-w-[88%] ${m.role === "user" ? "ml-auto" : ""}`}>
                              <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-md bg-gradient-to-r from-sky-600 to-emerald-500 text-white" : "rounded-bl-md border border-sky-100 bg-slate-50 text-slate-700"}`}>{m.text}</div>
                              <div className={`mt-1 px-1 text-[11px] text-slate-400 ${m.role === "user" ? "text-right" : "text-left"}`}>{m.time}</div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : isLoadingChat ? (
          <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-7">
            <div className="rounded-2xl border border-sky-100 bg-white px-4 py-10 text-center text-sm text-slate-500">Loading your messages...</div>
          </div>
        ) : (
          <div className="mx-auto mb-3 flex h-[calc(100dvh-7.75rem)] w-full max-w-5xl flex-col overflow-hidden border-y border-sky-100 bg-white sm:mb-4 sm:rounded-3xl sm:border sm:shadow-xl sm:shadow-sky-100/70 lg:mb-0 lg:h-[calc(100dvh-1.5rem)]">
            <div className="relative border-b border-sky-100 bg-gradient-to-r from-sky-50 to-emerald-50 px-4 py-3 sm:px-5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-sky-200 bg-white text-xs font-bold text-sky-700">{getInitials(user.name)}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800 sm:text-base">{user.name}</div>
                    <div className="text-xs font-medium text-emerald-600">Online</div>
                  </div>
                </div>
                <button
                  className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                  onClick={() => setShowSettings((v) => !v)}
                  aria-label="Open chat settings"
                >
                  Settings
                </button>
              </div>
              {showSettings && (
                <div className="absolute left-3 right-3 top-[calc(100%-4px)] z-30 rounded-2xl border border-sky-100 bg-white p-3 shadow-xl shadow-sky-100/80 sm:left-auto sm:right-4 sm:w-64">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Chat Settings</div>
                  <div className="mb-1 text-sm text-slate-700">Name: {user.name}</div>
                  <div className="text-sm text-slate-700">Email: {user.email}</div>
                  <button className="mt-3 w-full rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100" onClick={onLogout}>
                    Sign out
                  </button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-white to-sky-50/60 px-3 py-4 sm:px-5">
              <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
                {noConv ? (
                  <div className="mt-8 rounded-2xl border border-dashed border-sky-200 bg-white/70 px-4 py-10 text-center text-sm text-slate-500">
                    <div className="mb-3 text-3xl opacity-30">○</div>
                    <div>No question from your doctor.</div>
                    <div className="mt-2 text-xs">
                      Your doctor will start a check-in conversation here.
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((m, i) => (
                      <div key={i} className={`max-w-[88%] ${m.role === "user" ? "ml-auto" : ""}`}>
                        <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-md bg-gradient-to-r from-sky-600 to-emerald-500 text-white" : "rounded-bl-md border border-sky-100 bg-white text-slate-700"}`}>{m.text}</div>
                        <div className={`mt-1 px-1 text-[11px] text-slate-400 ${m.role === "user" ? "text-right" : "text-left"}`}>{m.time}</div>
                      </div>
                    ))}
                    {isTyping && (
                      <div className="w-fit max-w-[70%]">
                        <div className="inline-flex items-center rounded-2xl rounded-bl-md border border-sky-100 bg-white px-2.5 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:0ms]" />
                            <div className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:150ms]" />
                            <div className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:300ms]" />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
            <div className="flex gap-2 border-t border-sky-100 bg-white px-3 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-5">
              <input
                className="flex-1 rounded-xl border border-sky-100 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Write a message..."
                disabled={isTyping || noConv}
              />
              <button
                className={`grid h-11 w-11 shrink-0 place-items-center rounded-full text-base font-bold text-white transition ${input.trim() ? "bg-gradient-to-r from-sky-600 to-emerald-500 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sky-200" : "bg-sky-200"} disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0`}
                onClick={sendMessage}
                disabled={isTyping || !input.trim() || noConv}
                aria-label="Send message"
              >
                <span>➤</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <ErrorModal message={uiError} onClose={() => setUiError("")} />
    </div>
  );
};
